import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Table, Badge, ProgressBar, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faArrowDown, faArrowUp, faTags, faUpload, faChartLine, faBan, faCheckCircle, faSync, faUserSlash,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import api from '../api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// Repricing IBS — una schermata, un solo "Analizza".
// Il prezzo IBS è prezzo base + ricarico, con spedizione a carico del cliente (impostata in Mirakl). Qui il prezzo
// dell'articolo viene portato a battere il concorrente più economico (totale consegnato), senza scendere sotto il
// margine minimo. IBS stessa non è un concorrente (è sempre in buy box).

const CONDIZIONI = [
    { value: 'nuovo', label: 'Nuovo' },
    { value: 'usato_ottime', label: 'Usato ottime' },
    { value: 'usato_buone', label: 'Usato buone' },
];

const AZIONI = {
    ribasso: { label: 'Ribasso', bg: 'success', icon: faArrowDown },
    rialzo: { label: 'Rialzo', bg: 'primary', icon: faArrowUp },
    invariato: { label: 'Invariato', bg: 'secondary' },
    non_battibile: { label: 'Non battibile in utile', bg: 'warning', text: 'dark', icon: faBan },
    nessun_concorrente: { label: 'Nessun concorrente', bg: 'light', text: 'dark' },
};

const euro = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}€`);
// Codice stato Mirakl dalla condizione ("Nuovo", "Usato - Ottime Condizioni", "Usato - Buone Condizioni")
const stateCodeDa = (condizione) => {
    const c = (condizione || '').toLowerCase();
    if (c.includes('ottime')) return '2';
    if (c.includes('buone')) return '3';
    return '11';
};

function AzioneBadge({ azione }) {
    const a = AZIONI[azione] || { label: azione, bg: 'secondary' };
    return (
        <Badge bg={a.bg} text={a.text}>
            {a.icon && <FontAwesomeIcon icon={a.icon} className="me-1" />}{a.label}
        </Badge>
    );
}

const RepricingIBSPage = () => {
    // --- Parametri ---
    const [tipi, setTipi] = useState({ film: true, musica: true });
    const [condizioni, setCondizioni] = useState(new Set(CONDIZIONI.map(c => c.value)));
    const [sconto, setSconto] = useState(0.10);
    const [margineMinimo, setMargineMinimo] = useState('');
    const [alza, setAlza] = useState(true);
    const [maxDelta, setMaxDelta] = useState('');
    const [impostazioni, setImpostazioni] = useState(null);

    // --- Concorrenti da ignorare (elenco fisso) ---
    const [ignorati, setIgnorati] = useState([]);
    const [conosciuti, setConosciuti] = useState([]);
    const [showIgnorati, setShowIgnorati] = useState(false);
    const [nuovoIgnorato, setNuovoIgnorato] = useState('');

    // --- Analisi ---
    const [job, setJob] = useState(null);           // stato del job (polling)
    const [jobId, setJobId] = useState(null);
    const pollRef = useRef(null);
    const [filtroAzione, setFiltroAzione] = useState('tutti');
    const [selected, setSelected] = useState(new Set());

    // --- Applicazione ---
    const [applying, setApplying] = useState(false);
    const [importId, setImportId] = useState(null);
    const [importStatus, setImportStatus] = useState(null);

    // --- Storico ---
    const [historyModal, setHistoryModal] = useState({ show: false, ean: null, title: '', stateCode: '2' });
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [imp, ign, comp] = await Promise.all([
                    api.get('/impostazioni/prezzi/'),
                    api.get('/mirakl/repricing/concorrenti-ignorati'),
                    api.get('/mirakl/repricing/competitors').catch(() => ({ data: [] })),
                ]);
                setImpostazioni(imp.data);
                setIgnorati(ign.data || []);
                setConosciuti(comp.data || []);
            } catch (e) {
                toast.error('Impossibile caricare le impostazioni del repricing');
            }
        })();
        const salvato = sessionStorage.getItem('repricing_job_id');
        if (salvato) {
            setJobId(salvato);
        }
        return () => clearInterval(pollRef.current);
    }, []);

    // --- Polling del job ---
    useEffect(() => {
        if (!jobId) return undefined;
        const leggi = async () => {
            try {
                const res = await api.get(`/mirakl/repricing/analisi/${jobId}`);
                setJob(res.data);
                if (res.data.status !== 'running') {
                    clearInterval(pollRef.current);
                    if (res.data.status === 'done') {
                        setSelected(new Set((res.data.results || [])
                            .filter(r => r.azione === 'ribasso' || r.azione === 'rialzo')
                            .map(r => `${r.tipo}:${r.id}`)));
                    }
                    if (res.data.status === 'error') toast.error(`Analisi fallita: ${res.data.error}`);
                }
            } catch (e) {
                clearInterval(pollRef.current);
                sessionStorage.removeItem('repricing_job_id');
                setJob(null);
                setJobId(null);
            }
        };
        leggi();
        clearInterval(pollRef.current);
        pollRef.current = setInterval(leggi, 3000);
        return () => clearInterval(pollRef.current);
    }, [jobId]);

    const avvia = async () => {
        const tipiScelti = Object.keys(tipi).filter(k => tipi[k]);
        if (tipiScelti.length === 0 || condizioni.size === 0) {
            toast.warn('Scegli almeno un tipo di prodotto e una condizione');
            return;
        }
        setImportId(null);
        setImportStatus(null);
        setSelected(new Set());
        setFiltroAzione('tutti');
        try {
            const res = await api.post('/mirakl/repricing/analizza', {
                tipi: tipiScelti,
                condizioni: condizioni.size === CONDIZIONI.length ? null : Array.from(condizioni),
                sconto: parseFloat(sconto) || 0,
                margine_minimo: margineMinimo === '' ? null : parseFloat(margineMinimo),
                alza_sottoprezzati: alza,
                max_delta: maxDelta === '' ? null : parseFloat(maxDelta),
            });
            sessionStorage.setItem('repricing_job_id', res.data.job_id);
            setJob({ status: 'running', fase: 'Avvio', progress: 0, total: null, results: [] });
            setJobId(res.data.job_id);
        } catch (e) {
            toast.error(`Impossibile avviare l'analisi: ${e.response?.data?.detail || e.message}`);
        }
    };

    const ferma = async () => {
        try {
            await api.post(`/mirakl/repricing/analisi/${jobId}/cancel`);
        } catch (e) { /* già terminata */ }
    };

    // --- Applicazione ---
    const risultati = job?.results || [];
    const righeFiltrate = useMemo(
        () => risultati.filter(r => filtroAzione === 'tutti' || r.azione === filtroAzione),
        [risultati, filtroAzione],
    );
    const aggiornabili = useMemo(() => risultati.filter(r => r.azione === 'ribasso' || r.azione === 'rialzo'), [risultati]);
    const chiave = (r) => `${r.tipo}:${r.id}`;

    const applica = async () => {
        const scelti = aggiornabili.filter(r => selected.has(chiave(r)));
        if (scelti.length === 0) {
            toast.warn('Nessun prodotto selezionato');
            return;
        }
        if (!window.confirm(`Applicare ${scelti.length} nuovi prezzi IBS? Vengono salvati e inviati subito a Mirakl.`)) return;
        setApplying(true);
        try {
            const res = await api.post('/mirakl/repricing/apply', {
                updates: scelti.map(r => ({ tipo: r.tipo, id: r.id, new_price: r.prezzo_suggerito })),
            });
            setImportId(res.data.import_id);
            toast.success(res.data.message);
            if (res.data.errori?.length) toast.warn(`${res.data.errori.length} prodotti saltati`);
        } catch (e) {
            toast.error(`Errore nell'applicazione: ${e.response?.data?.detail || e.message}`);
        } finally {
            setApplying(false);
        }
    };

    const controllaImport = async () => {
        try {
            const res = await api.get(`/mirakl/offers/import-status/${importId}`);
            setImportStatus(res.data);
        } catch (e) {
            toast.error("Impossibile leggere lo stato dell'importazione");
        }
    };

    const toggle = (r) => setSelected(prev => {
        const n = new Set(prev);
        if (n.has(chiave(r))) n.delete(chiave(r)); else n.add(chiave(r));
        return n;
    });
    const tuttiSelezionati = aggiornabili.length > 0 && aggiornabili.every(r => selected.has(chiave(r)));
    const toggleTutti = () => setSelected(tuttiSelezionati ? new Set() : new Set(aggiornabili.map(chiave)));

    // --- Concorrenti ignorati ---
    const salvaIgnorati = async (lista) => {
        try {
            const res = await api.put('/mirakl/repricing/concorrenti-ignorati', { concorrenti: lista });
            setIgnorati(res.data);
            toast.success('Elenco concorrenti da ignorare salvato');
        } catch (e) {
            toast.error('Salvataggio non riuscito');
        }
    };
    const aggiungiIgnorato = () => {
        const nome = nuovoIgnorato.trim();
        if (!nome) return;
        const trovato = conosciuti.find(c => c.shop_name.toLowerCase() === nome.toLowerCase());
        if (ignorati.some(i => i.shop_name.toLowerCase() === nome.toLowerCase())) return;
        salvaIgnorati([...ignorati, { shop_id: trovato?.shop_id || '', shop_name: trovato?.shop_name || nome }]);
        setNuovoIgnorato('');
    };

    // --- Storico ---
    const apriStorico = useCallback(async (ean, titolo, stateCode) => {
        setHistoryModal({ show: true, ean, title: titolo, stateCode });
        setHistoryLoading(true);
        setHistoryData([]);
        try {
            const res = await api.get(`/mirakl/repricing/history/${ean}`, { params: { state_code: stateCode } });
            setHistoryData(res.data.points || []);
        } catch {
            toast.error('Impossibile caricare lo storico prezzi');
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    const inCorso = job?.status === 'running';
    const finito = job && job.status !== 'running';
    const riepilogo = job?.riepilogo;
    const percentuale = job?.total ? Math.min(100, Math.round(((job.progress || 0) / job.total) * 100)) : 0;
    const feeMedio = impostazioni?.ibs_spedizione_cliente ?? 5.9;
    const feePiccolo = impostazioni?.ibs_spedizione_piccolo;
    const sogliaClasse = impostazioni?.ibs_soglia_classe_piccolo ?? 8;
    const testoSpedizione = feePiccolo != null
        ? `${feePiccolo.toFixed(2)}€ (articoli sotto ${sogliaClasse}€) o ${feeMedio.toFixed(2)}€`
        : `${feeMedio.toFixed(2)}€`;

    return (
        <>
            <Container fluid className="py-4">
                <Row className="mb-3 align-items-center">
                    <Col>
                        <h1 className="h3 mb-0"><FontAwesomeIcon icon={faTags} className="me-2 text-primary" />Repricing IBS</h1>
                        <p className="text-muted mb-0 small">
                            Porta il prezzo IBS a battere il concorrente più economico. Si confronta il <strong>totale che paga il cliente</strong>:
                            il nostro prezzo + {testoSpedizione} di spedizione contro articolo + spedizione del concorrente.
                            Mai sotto il margine minimo. IBS stessa è sempre in buy box e non viene considerata.
                        </p>
                    </Col>
                </Row>

                <Card className="mb-3 shadow-sm">
                    <Card.Body>
                        <Row className="g-3">
                            <Col md={3}>
                                <Form.Label className="fw-semibold">Prodotti</Form.Label>
                                <div>
                                    <Form.Check inline type="checkbox" label="Film" checked={tipi.film} onChange={e => setTipi({ ...tipi, film: e.target.checked })} />
                                    <Form.Check inline type="checkbox" label="Musica" checked={tipi.musica} onChange={e => setTipi({ ...tipi, musica: e.target.checked })} />
                                </div>
                                <div className="mt-1">
                                    {CONDIZIONI.map(c => (
                                        <Form.Check
                                            inline key={c.value} type="checkbox" label={c.label} checked={condizioni.has(c.value)}
                                            onChange={e => setCondizioni(prev => { const n = new Set(prev); if (e.target.checked) n.add(c.value); else n.delete(c.value); return n; })}
                                        />
                                    ))}
                                </div>
                                <Form.Text className="text-muted">Solo prodotti pubblicati su IBS con stock.</Form.Text>
                            </Col>
                            <Col md={2}>
                                <Form.Label className="fw-semibold">Batti il concorrente di</Form.Label>
                                <div className="input-group">
                                    <Form.Control type="number" min={0} step={0.05} value={sconto} onChange={e => setSconto(e.target.value)} />
                                    <span className="input-group-text">€</span>
                                </div>
                                <Form.Text className="text-muted">sul totale consegnato</Form.Text>
                            </Col>
                            <Col md={2}>
                                <Form.Label className="fw-semibold">Margine minimo</Form.Label>
                                <div className="input-group">
                                    <Form.Control
                                        type="number" min={0} step={0.1} value={margineMinimo}
                                        placeholder={impostazioni ? String(impostazioni.ibs_margine_minimo) : ''}
                                        onChange={e => setMargineMinimo(e.target.value)}
                                    />
                                    <span className="input-group-text">€</span>
                                </div>
                                <Form.Text className="text-muted">netto a vendita (vuoto = impostazioni)</Form.Text>
                            </Col>
                            <Col md={3}>
                                <Form.Label className="fw-semibold">Opzioni</Form.Label>
                                <Form.Check
                                    type="switch" id="alza" checked={alza} onChange={e => setAlza(e.target.checked)}
                                    label="Alza dove costiamo ≥1€ meno del mercato"
                                />
                                <div className="input-group input-group-sm mt-1">
                                    <span className="input-group-text">Variazione max</span>
                                    <Form.Control type="number" min={0} step={0.5} value={maxDelta} placeholder="nessuna" onChange={e => setMaxDelta(e.target.value)} />
                                    <span className="input-group-text">€</span>
                                </div>
                            </Col>
                            <Col md={2} className="d-flex flex-column justify-content-end">
                                {inCorso ? (
                                    <Button variant="outline-danger" onClick={ferma}>Ferma</Button>
                                ) : (
                                    <Button variant="primary" size="lg" onClick={avvia}>
                                        <FontAwesomeIcon icon={faSearch} className="me-2" />Analizza
                                    </Button>
                                )}
                                <Button variant="link" size="sm" className="mt-1" onClick={() => setShowIgnorati(true)}>
                                    <FontAwesomeIcon icon={faUserSlash} className="me-1" />Concorrenti ignorati ({ignorati.length})
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {inCorso && (
                    <Card className="mb-3">
                        <Card.Body>
                            <div className="d-flex justify-content-between small mb-1">
                                <span><Spinner animation="border" size="sm" className="me-2" />{job.fase}</span>
                                <span>{job.total ? `${job.progress || 0} / ${job.total} prodotti` : ''}</span>
                            </div>
                            <ProgressBar now={percentuale} animated />
                            <div className="text-muted small mt-1">Lo scarico dei prezzi di mercato richiede un paio di minuti. Puoi lasciare la pagina: l'analisi prosegue.</div>
                        </Card.Body>
                    </Card>
                )}

                {finito && job.status === 'cancelled' && <Alert variant="secondary">Analisi interrotta.</Alert>}
                {finito && job.status === 'error' && <Alert variant="danger">Analisi fallita: {job.error}</Alert>}

                {job?.status === 'done' && riepilogo && (
                    <>
                        <Row className="g-2 mb-3">
                            {[
                                ['ribasso', riepilogo.ribasso], ['rialzo', riepilogo.rialzo], ['non_battibile', riepilogo.non_battibile],
                                ['invariato', riepilogo.invariato], ['nessun_concorrente', riepilogo.nessun_concorrente],
                            ].map(([az, n]) => (
                                <Col key={az} xs={6} md>
                                    <div
                                        role="button"
                                        onClick={() => setFiltroAzione(filtroAzione === az ? 'tutti' : az)}
                                        className={`border rounded p-2 text-center ${filtroAzione === az ? 'border-primary bg-light' : ''}`}
                                    >
                                        <div className="fs-4 fw-bold">{n}</div>
                                        <AzioneBadge azione={az} />
                                    </div>
                                </Col>
                            ))}
                            <Col xs={6} md>
                                <div className="border rounded p-2 text-center">
                                    <div className="fs-4 fw-bold">{riepilogo.margine_medio_suggerito !== null ? euro(riepilogo.margine_medio_suggerito) : '—'}</div>
                                    <small className="text-muted">margine medio dei prezzi suggeriti</small>
                                </div>
                            </Col>
                        </Row>

                        <Card className="shadow-sm">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <span>
                                    {righeFiltrate.length} prodotti{filtroAzione !== 'tutti' ? ` (${AZIONI[filtroAzione]?.label})` : ''}
                                    {filtroAzione !== 'tutti' && <Button variant="link" size="sm" onClick={() => setFiltroAzione('tutti')}>mostra tutti</Button>}
                                </span>
                                <Button variant="success" disabled={applying || selected.size === 0} onClick={applica}>
                                    {applying ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={faUpload} className="me-1" />}
                                    {' '}Applica {selected.size} prezzi su Mirakl
                                </Button>
                            </Card.Header>
                            <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
                                <Table size="sm" hover className="mb-0 align-middle" style={{ fontSize: '0.85rem' }}>
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th style={{ width: 36 }}><Form.Check checked={tuttiSelezionati} onChange={toggleTutti} disabled={aggiornabili.length === 0} /></th>
                                            <th>Prodotto</th>
                                            <th className="text-end">Prezzo IBS</th>
                                            <th className="text-end" title="Prezzo IBS + spedizione al cliente">Totale cliente</th>
                                            <th className="text-end" title="Articolo + spedizione del concorrente più economico">Mercato (totale)</th>
                                            <th>Concorrente</th>
                                            <th className="text-end">Nuovo prezzo IBS</th>
                                            <th className="text-end">Nuovo totale</th>
                                            <th className="text-end" title="Margine netto per vendita al nuovo prezzo (dopo commissione, spedizione, costo merce)">Margine</th>
                                            <th>Azione</th>
                                            <th>Motivo</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {righeFiltrate.slice(0, 1500).map(r => (
                                            <tr key={chiave(r)}>
                                                <td>
                                                    {(r.azione === 'ribasso' || r.azione === 'rialzo')
                                                        ? <Form.Check checked={selected.has(chiave(r))} onChange={() => toggle(r)} /> : null}
                                                </td>
                                                <td>
                                                    <div className="fw-semibold">{r.titolo}</div>
                                                    <small className="text-muted font-monospace">{r.sku}</small>
                                                    <Badge bg="light" text="dark" className="ms-1">{r.tipo === 'musica' ? 'Musica' : 'Film'}</Badge>
                                                </td>
                                                <td className="text-end">{euro(r.prezzo_attuale)}</td>
                                                <td className="text-end">{euro(r.totale_attuale)}</td>
                                                <td className="text-end">{euro(r.mercato_totale)}</td>
                                                <td>{r.concorrente || <span className="text-muted">—</span>}</td>
                                                <td className="text-end fw-bold">{r.prezzo_suggerito !== null ? euro(r.prezzo_suggerito) : '—'}</td>
                                                <td className="text-end">{euro(r.totale_suggerito)}</td>
                                                <td className="text-end">{r.margine_suggerito !== null ? euro(r.margine_suggerito) : <span className="text-muted">{euro(r.margine_attuale)}</span>}</td>
                                                <td><AzioneBadge azione={r.azione} /></td>
                                                <td style={{ maxWidth: 320, fontSize: '0.78rem' }} className="text-muted">{r.motivo}</td>
                                                <td>
                                                    <Button
                                                        variant="outline-secondary" size="sm" title="Storico del prezzo di mercato"
                                                        onClick={() => apriStorico(r.barcode_ibs, r.titolo, stateCodeDa(r.condizione))}
                                                    >
                                                        <FontAwesomeIcon icon={faChartLine} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                {righeFiltrate.length > 1500 && (
                                    <div className="text-center text-muted small py-2">Mostrati i primi 1500: usa i filtri (clic sui riquadri) per restringere.</div>
                                )}
                            </div>
                        </Card>
                    </>
                )}

                {importId && (
                    <Card className="mt-3">
                        <Card.Header><FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />Prezzi inviati a Mirakl — importazione {importId}</Card.Header>
                        <Card.Body>
                            <Button variant="outline-secondary" size="sm" className="mb-2" onClick={controllaImport}>
                                <FontAwesomeIcon icon={faSync} className="me-1" />Controlla esito
                            </Button>
                            {importStatus && (
                                <Alert variant={importStatus.status === 'COMPLETE' || importStatus.import_state === 'COMPLETE' ? 'success' : 'info'} className="py-2 mb-0">
                                    Stato: <strong>{importStatus.status || importStatus.import_state || '—'}</strong>
                                    {' · '}righe lette {importStatus.lines_read ?? importStatus.total_count ?? '—'}
                                    {' · '}riuscite {importStatus.lines_in_success ?? '—'}
                                    {' · '}in errore {importStatus.lines_in_error ?? importStatus.error_count ?? '—'}
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                )}
            </Container>

            {/* Concorrenti da ignorare */}
            <Modal show={showIgnorati} onHide={() => setShowIgnorati(false)} centered>
                <Modal.Header closeButton><Modal.Title style={{ fontSize: '1rem' }}>Concorrenti da ignorare</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p className="text-muted small">
                        Questi venditori non vengono mai considerati nel confronto (per esempio rivenditori con prezzi anomali).
                        L'elenco vale per tutte le analisi. Il nostro negozio è già escluso in automatico.
                    </p>
                    {ignorati.length === 0 && <div className="text-muted">Nessun venditore ignorato.</div>}
                    {ignorati.map(c => (
                        <div key={c.shop_name} className="d-flex justify-content-between align-items-center border-bottom py-1">
                            <span>{c.shop_name}</span>
                            <Button variant="outline-danger" size="sm" onClick={() => salvaIgnorati(ignorati.filter(i => i.shop_name !== c.shop_name))}>Rimuovi</Button>
                        </div>
                    ))}
                    <div className="input-group mt-3">
                        <Form.Control
                            list="elenco-venditori" placeholder="Nome del venditore" value={nuovoIgnorato}
                            onChange={e => setNuovoIgnorato(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') aggiungiIgnorato(); }}
                        />
                        <datalist id="elenco-venditori">{conosciuti.map(c => <option key={c.shop_id} value={c.shop_name} />)}</datalist>
                        <Button variant="primary" onClick={aggiungiIgnorato}>Aggiungi</Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Storico prezzi di mercato */}
            <Modal show={historyModal.show} onHide={() => setHistoryModal(m => ({ ...m, show: false }))} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1rem' }}>
                        <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />Storico prezzi &mdash; {historyModal.title}
                        {historyModal.ean && <small className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>({historyModal.ean})</small>}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historyLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                    ) : historyData.length === 0 ? (
                        <Alert variant="info">Nessuno storico disponibile: si accumula a ogni analisi.</Alert>
                    ) : (
                        <div style={{ position: 'relative', height: '300px' }}>
                            <Line
                                data={{
                                    labels: historyData.map(p => p.ts.slice(0, 16).replace('T', ' ')),
                                    datasets: [{
                                        label: 'Prezzo mercato consegnato (€)', data: historyData.map(p => p.price),
                                        borderColor: '#0d6efd', backgroundColor: 'rgba(13,110,253,0.08)', fill: true, tension: 0.3, spanGaps: true,
                                    }],
                                }}
                                options={{
                                    responsive: true, maintainAspectRatio: false,
                                    plugins: { legend: { display: false } },
                                    scales: { y: { ticks: { callback: v => `${Number(v).toFixed(2)}€` } }, x: { ticks: { maxTicksLimit: 10, maxRotation: 45 } } },
                                }}
                            />
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default RepricingIBSPage;
